import { Message } from "@apis:message/message";
import { post } from "@libs:axios";
import { STREAMING_CURSOR } from "@pages:app/talk/const";
import { camelizeObjectKeys } from "@utils:general";
import ndjsonStream from "can-ndjson-stream";
import { get } from "lodash";

interface StreamAIProps {
  setReferences: (data: Message["GET_ONE"]["Response"]["docs"]) => void;
  setAnswer: (data: (prev: string) => string) => void;
  setMessage?: (data: Message["GET_ONE"]["Response"]) => void;
  onNoResponse?: () => void;
}

interface AskAIStreamParams {
  question: string;
  botId: string;
  previousConversationId?: string;
}

export const useStreamAI = ({
  setReferences,
  setAnswer,
  setMessage,
  onNoResponse,
}: StreamAIProps) => {
  let isCancelled = false;
  let controller: AbortController;

  const askAIStream = async ({
    question,
    botId,
    previousConversationId,
  }: AskAIStreamParams) => {
    isCancelled = false;
    controller = new AbortController();
    const signal = controller.signal;

    const payload: Record<string, unknown> = {
      question,
    };

    if (previousConversationId && !previousConversationId.startsWith("new")) {
      payload.conversation_id = previousConversationId;
    }

    let resp: ReadableStream<Uint8Array>;
    try {
      resp = await post<ReadableStream<Uint8Array>>(
        "/api/search/stream",
        payload,
        {
          params: {
            botId: botId,
          },
          signal,
          adapter: "fetch",
          responseType: "stream",
        },
      );
    } catch (error) {
      // A cancelled request is not a failure
      if (signal.aborted) {
        return;
      }

      if (onNoResponse) {
        onNoResponse();
        return;
      }

      throw error;
    }

    try {
      const reader = ndjsonStream(resp).getReader();
      let result = await reader.read();

      if (result.done) {
        onNoResponse?.();
        return;
      }

      while (!result || !result.done || isCancelled) {
        if (!result.value) continue;

        if (get(result.value, "error")) {
          throw new Error("stream failed");
        }

        const references = get(result.value, "docs");
        if (references) {
          setReferences(references);
        }

        const isMessageExist = get(result.value, "id");
        if (isMessageExist) {
          setMessage?.(
            camelizeObjectKeys(
              result.value,
            ) as unknown as Message["GET_ONE"]["Response"],
          );

          // The saved message means the answer is complete.
          // Drop the streaming cursor (bullet) now instead of waiting for the stream to close.
          setAnswer((prev) =>
            prev.endsWith(STREAMING_CURSOR)
              ? prev.slice(0, -STREAMING_CURSOR.length)
              : prev,
          );
        }

        const chunk = get(result.value, "chunk");
        if (chunk) {
          setAnswer((prev) => {
            const base = prev.endsWith(STREAMING_CURSOR)
              ? prev.slice(0, -STREAMING_CURSOR.length)
              : prev;

            return base + chunk + STREAMING_CURSOR;
          });
        }
        result = await reader.read();
      }

      // Remove the streaming cursor after processing all elements
      setAnswer((prev) =>
        prev.endsWith(STREAMING_CURSOR)
          ? prev.slice(0, -STREAMING_CURSOR.length)
          : prev,
      );
    } catch (error) {
      // The stream aborted mid-run (e.g. provider error after the response headers were sent).
      if (signal.aborted || isCancelled) {
        return;
      }

      if (onNoResponse) {
        onNoResponse();
        return;
      }

      throw error;
    }
  };

  const cancel = async () => {
    controller?.abort();
    isCancelled = true;
  };

  return {
    askAIStream,
    cancel,
  };
};
